import { domainError, type DomainError } from '../../shared/domain-error';
import { normalizeName } from '../../shared/naming';
import { err, ok, type Result } from '../../shared/result';
import type { RowIssue } from '../ganttpro/errors';
import {
  cleanText,
  columnOf,
  readDate,
  readNumber,
  readWbsList,
  type Matrix,
} from '../ganttpro/matrix-cells';
import { PURCHASE_TEMPLATE_ERROR } from './errors';
import { findHeader, headerKey, type RequiredColumn } from './header';
import type {
  AllocationBasisData,
  MaterialTotalData,
  PurchaseKindData,
  PurchaseLineRowData,
} from './row-data';

/**
 * Leitura da aba **"Itens de compra"** da planilha de itens de compra (Fase 26).
 *
 * A planilha foi gerada fora do sistema, a partir do orçamento do Park Europa, e traz uma
 * linha por (pavimento × disciplina × serviço × material) com a data de necessidade, o
 * centro de custo, o método de cálculo e o valor. O sistema **não guarda preço** (RN-173,
 * D7): `preco_unit`, `valor`, `composicao` e `centro_de_custo` ficam só na planilha, e o
 * leitor os ignora. O centro de custo, aliás, é a disciplina e o serviço da própria linha
 * (RN-348) — não há campo à parte.
 *
 * Como o leitor do cronograma por item (Fase 25), é função pura sobre a matriz crua
 * (`unknown[][]`): o cabeçalho é achado pelo nome, em `snake_case` ou com o rótulo humano,
 * e o que está errado numa linha só vira `issue`, com o número da linha do Excel, sem
 * derrubar o arquivo.
 *
 * ## O que entra no sistema
 *
 * - **só a linha com `vai_para_sistema` = Sim** (D6, D8). `Não` — equipamento, despesa
 *   indireta, seguro, risco — e `CONFIRMAR` — o bloco repetido da telefonia — vão para
 *   `skipped`, com o motivo, e nunca para `rows`;
 * - **`kind`** pela classe: MAT (e as listas das instalações) é `MATERIAL`; ST e ST-MAT são
 *   `SERVICO_TERCEIRIZADO` (RN-349);
 * - **`allocationBasis`** pelo método: `CPU×Q_T2` é `DIRETO`, `obra×169/481` é
 *   `RATEIO_OBRA`, `rateio por andar` é `RATEIO_ANDARES` e `lista Mat T2` é `LISTA` (RN-345);
 * - **`dateFollowsItem`**: a data segue o item (RN-342) quando a linha tem `edt_do_item` e
 *   não é rateio por andar nem do pavimento Lazer — o rateio e o lazer não têm item de
 *   cronograma que lhes diga a data, e guardam a que a planilha calculou;
 * - **RN-352**: as linhas da mesma (torre, pavimento, disciplina, serviço, código) — as
 *   faces da fachada, uma por trecho, e o mesmo material vindo de duas composições do
 *   orçamento — se **somam** numa só. No servidor a repetição é recusada (RN-037), e o
 *   trecho não existe na linha de compra. A soma leva a menor data, é provisória se alguma
 *   parte o for, fica com a base de cálculo menos firme e diz na observação quantas se
 *   juntaram. Unidade ou classe diferentes não se somam: a segunda vira problema.
 */

/**
 * As grafias aceitas de cada coluna, já em chave (`headerKey`). `centro_de_custo`,
 * `preco_unit`, `valor` e `composicao` não estão aqui de propósito: não são lidas.
 */
export const PURCHASE_LINE_HEADERS = {
  code: ['codigo', 'cod', 'codigo do material', 'codigo material'],
  material: ['material', 'descricao', 'descricao do material', 'insumo'],
  materialClass: ['classe', 'classe do insumo'],
  category: ['grupo tema', 'grupo', 'tema', 'familia'],
  unit: ['unidade', 'un', 'und'],
  tower: ['torre'],
  floor: ['pavimento', 'andar'],
  level: ['nivel'],
  discipline: ['disciplina'],
  service: ['servico'],
  segment: ['trecho'],
  quantity: ['quantidade', 'qtd', 'quantidade t2', 'quantidade torre 2'],
  requiredDate: ['necessario em', 'data de necessidade', 'data necessaria', 'necessidade'],
  wbs: ['edt do item', 'edt item', 'edt'],
  method: ['metodo', 'metodo de calculo', 'base do rateio'],
  provisional: ['provisoria', 'quantidade provisoria'],
  forSystem: ['vai para sistema', 'vai para o sistema', 'vai pro sistema', 'entra no sistema'],
  notes: ['observacao', 'observacoes', 'obs'],
} as const;

/** As colunas sem as quais a leitura trocaria uma regra por um silêncio. */
const REQUIRED: readonly RequiredColumn[] = [
  { label: 'codigo', spellings: PURCHASE_LINE_HEADERS.code },
  { label: 'material', spellings: PURCHASE_LINE_HEADERS.material },
  { label: 'classe', spellings: PURCHASE_LINE_HEADERS.materialClass },
  { label: 'unidade', spellings: PURCHASE_LINE_HEADERS.unit },
  { label: 'torre', spellings: PURCHASE_LINE_HEADERS.tower },
  { label: 'pavimento', spellings: PURCHASE_LINE_HEADERS.floor },
  { label: 'disciplina', spellings: PURCHASE_LINE_HEADERS.discipline },
  { label: 'servico', spellings: PURCHASE_LINE_HEADERS.service },
  { label: 'quantidade', spellings: PURCHASE_LINE_HEADERS.quantity },
  { label: 'necessario_em', spellings: PURCHASE_LINE_HEADERS.requiredDate },
  { label: 'edt_do_item', spellings: PURCHASE_LINE_HEADERS.wbs },
  { label: 'metodo', spellings: PURCHASE_LINE_HEADERS.method },
  { label: 'provisoria', spellings: PURCHASE_LINE_HEADERS.provisional },
  { label: 'vai_para_sistema', spellings: PURCHASE_LINE_HEADERS.forSystem },
];

const SCAN_LIMIT = 30;

/**
 * Os tamanhos que o contrato (`purchaseLineRowSchema`) aceita. O servidor recusa o pedido
 * inteiro quando uma só linha estoura — por isso o leitor aponta a linha antes, e as outras
 * seguem.
 */
const LIMITS = {
  code: 30,
  material: 200,
  unit: 12,
  tower: 80,
  floor: 80,
  discipline: 160,
  service: 160,
  category: 80,
  notes: 500,
} as const;

/** Como a coluna Serviço escreve a frente inteira (o mesmo texto do modelo da Fase 25). */
const WHOLE_FRONT = new Set(['(frente inteira)', 'frente inteira']);

/** O pavimento do lazer: a data dele não segue item (D3). */
const LEISURE_FLOOR = 'lazer';

/** Por que uma linha ficou de fora do sistema sem ser erro. */
export type SkipCause = 'NOT_FOR_SYSTEM' | 'TO_CONFIRM';

export interface SkippedLine {
  /** Linha do Excel (a primeira é 1). */
  readonly rowNumber: number;
  readonly cause: SkipCause;
  readonly reason: string;
}

export interface PurchaseLinesReading {
  /** As linhas que entram, já com as faces da fachada somadas (RN-352). */
  readonly rows: PurchaseLineRowData[];
  /** `vai_para_sistema` = Não ou CONFIRMAR: ficam só na planilha, e a tela diz quantas. */
  readonly skipped: SkippedLine[];
  /** Linhas que não puderam ser lidas — as outras seguem. */
  readonly issues: RowIssue[];
  /**
   * RN-352: quantas linhas da planilha foram **somadas a outra** da mesma (frente, serviço,
   * material). As que entraram na leitura são `rows.length + unions`.
   */
  readonly unions: number;
  /**
   * O total de cada material **como as linhas da planilha o escrevem** (RN-350): a soma da
   * quantidade das linhas que entraram, antes de somar as faces. É o que a importação confere
   * contra a soma do que o servidor aceitou — a diferença são linhas recusadas na prévia —, e
   * tem a forma de `expectedTotals` do contrato. Não inclui as ignoradas nem as com problema.
   */
  readonly totals: MaterialTotalData[];
  /** Quantas linhas de `rows` nasceram de uma soma. */
  readonly unionGroups: number;
  readonly headerRowNumber: number;
}

/** A matriz tem o cabeçalho da aba "Itens de compra"? Serve para a tela escolher o leitor. */
export function looksLikePurchaseLines(matrix: Matrix): boolean {
  return findHeader(matrix, REQUIRED, SCAN_LIMIT).ok;
}

export function readPurchaseLinesTemplate(
  matrix: Matrix,
): Result<PurchaseLinesReading, DomainError> {
  const search = findHeader(matrix, REQUIRED, SCAN_LIMIT);
  if (!search.ok) {
    const labels = REQUIRED.map((column) => `"${column.label}"`).join(', ');
    return err(
      domainError(
        PURCHASE_TEMPLATE_ERROR.LINES_HEADER_NOT_FOUND,
        search.nearest === undefined
          ? `Não encontrei o cabeçalho da planilha de itens de compra — as colunas ${labels} — ` +
              `nas primeiras ${String(SCAN_LIMIT)} linhas desta aba. É a aba "Itens de compra"?`
          : `O cabeçalho da linha ${String(search.nearest.rowNumber)} não tem todas as colunas da ` +
              `planilha de itens de compra. Faltam: ${search.nearest.missing
                .map((label) => `"${label}"`)
                .join(', ')}.`,
        search.nearest === undefined ? undefined : { missing: search.nearest.missing },
      ),
    );
  }

  const { header } = search;
  const column = (spellings: readonly string[]) => columnOf(header.columns, spellings);
  const columns = {
    code: column(PURCHASE_LINE_HEADERS.code),
    material: column(PURCHASE_LINE_HEADERS.material),
    materialClass: column(PURCHASE_LINE_HEADERS.materialClass),
    category: column(PURCHASE_LINE_HEADERS.category),
    unit: column(PURCHASE_LINE_HEADERS.unit),
    tower: column(PURCHASE_LINE_HEADERS.tower),
    floor: column(PURCHASE_LINE_HEADERS.floor),
    level: column(PURCHASE_LINE_HEADERS.level),
    discipline: column(PURCHASE_LINE_HEADERS.discipline),
    service: column(PURCHASE_LINE_HEADERS.service),
    segment: column(PURCHASE_LINE_HEADERS.segment),
    quantity: column(PURCHASE_LINE_HEADERS.quantity),
    requiredDate: column(PURCHASE_LINE_HEADERS.requiredDate),
    wbs: column(PURCHASE_LINE_HEADERS.wbs),
    method: column(PURCHASE_LINE_HEADERS.method),
    provisional: column(PURCHASE_LINE_HEADERS.provisional),
    forSystem: column(PURCHASE_LINE_HEADERS.forSystem),
    notes: column(PURCHASE_LINE_HEADERS.notes),
  };

  const skipped: SkippedLine[] = [];
  const issues: RowIssue[] = [];
  const groups = new Map<string, Group>();
  const totals = new Map<string, MaterialTotalData>();
  /** O total do material como as linhas que entram o escrevem — antes de somar as faces. */
  const addTotal = (materialCode: string, quantity: number) => {
    const key = normalizeName(materialCode);
    const total = totals.get(key);
    if (total === undefined) totals.set(key, { materialCode, total: quantity });
    else total.total += quantity;
  };
  let unions = 0;

  for (let index = header.rowIndex + 1; index < matrix.length; index += 1) {
    const cells = matrix[index] ?? [];
    const rowNumber = index + 1;
    const at = (position: number | undefined): unknown =>
      position === undefined ? undefined : cells[position];

    const code = cleanText(at(columns.code));
    const materialName = cleanText(at(columns.material));
    const towerName = cleanText(at(columns.tower));
    const floorName = cleanText(at(columns.floor));
    const disciplineName = cleanText(at(columns.discipline));
    const materialClass = cleanText(at(columns.materialClass));
    const gate = readGate(at(columns.forSystem));

    // Linha em branco (ou de rodapé): sem a frente (torre, pavimento, disciplina) e sem
    // decisão no portão. Uma linha com frente e sem portão não é rodapé: é erro, e aparece.
    if (
      gate.kind === 'EMPTY' &&
      towerName === null &&
      floorName === null &&
      disciplineName === null
    ) {
      continue;
    }

    // O portão vem antes de tudo: o que fica só na planilha (equipamento, seguro, o bloco
    // repetido da telefonia) não precisa estar completo, e não é erro.
    if (gate.kind === 'NAO' || gate.kind === 'CONFIRMAR') {
      skipped.push({
        rowNumber,
        cause: gate.kind === 'NAO' ? 'NOT_FOR_SYSTEM' : 'TO_CONFIRM',
        reason: skipReason(gate.kind, materialName ?? code, materialClass, floorName),
      });
      continue;
    }

    const label = [materialName ?? code, floorName].filter((part) => part !== null).join(' / ');
    if (gate.kind !== 'SIM') {
      issues.push({
        rowNumber,
        message:
          `${label === '' ? 'Linha sem identificação' : label}: a coluna vai_para_sistema precisa ` +
          `ser Sim, Não ou CONFIRMAR${gate.kind === 'UNKNOWN' ? ` (veio "${gate.text}")` : ' (está vazia)'}.`,
      });
      continue;
    }

    const unit = cleanText(at(columns.unit));
    const absent = [
      ['Código', code],
      ['Material', materialName],
      ['Unidade', unit],
      ['Torre', towerName],
      ['Pavimento', floorName],
      ['Disciplina', disciplineName],
    ]
      .filter(([, value]) => value === null)
      .map(([name]) => name);
    if (
      absent.length > 0 ||
      code === null ||
      materialName === null ||
      unit === null ||
      towerName === null ||
      floorName === null ||
      disciplineName === null
    ) {
      issues.push({
        rowNumber,
        message: `${label === '' ? 'Linha' : label}: faltam ${absent.join(', ')}.`,
      });
      continue;
    }

    const serviceText = cleanText(at(columns.service));
    const serviceName =
      serviceText === null || WHOLE_FRONT.has(normalizeName(serviceText)) ? null : serviceText;
    const category = cleanText(at(columns.category));
    const method = cleanText(at(columns.method));
    const basis = method === null ? undefined : basisOf(method);
    const kind = kindOf(materialClass, basis);
    const quantity = readNumber(at(columns.quantity));
    const required = readDate(at(columns.requiredDate));
    const level = readNumber(at(columns.level));
    const notes = cleanText(at(columns.notes));

    const problems: string[] = [];
    if (kind === undefined) {
      problems.push(
        `a classe ${materialClass === null ? 'está vazia' : `"${materialClass}" não vai para o sistema`} ` +
          '(só MAT, ST e ST-MAT, mais as listas das instalações)',
      );
    }
    if (basis === undefined) {
      problems.push(
        `o método ${method === null ? 'está vazio' : `"${method}" não é reconhecido`} ` +
          '(CPU×Q_T2, obra×169/481, rateio por andar ou lista Mat T2)',
      );
    }
    let quantityValue: number | undefined;
    if (!quantity.ok) {
      problems.push(`a quantidade "${rawText(at(columns.quantity))}" não é um número`);
    } else if (quantity.value === null) {
      problems.push('a quantidade está vazia');
    } else if (quantity.value <= 0) {
      problems.push(`a quantidade ${String(quantity.value)} não é maior que zero`);
    } else {
      quantityValue = quantity.value;
    }
    let requiredDate: string | undefined;
    if (!required.ok) {
      problems.push(`a data de necessidade "${rawText(at(columns.requiredDate))}" não é uma data`);
    } else if (required.value === null) {
      problems.push('a data de necessidade está vazia');
    } else {
      requiredDate = required.value;
    }
    if (!level.ok || (level.value !== null && !Number.isInteger(level.value))) {
      problems.push(`o nível "${rawText(at(columns.level))}" não é um número inteiro`);
    }
    for (const [name, text, max] of [
      ['O código', code, LIMITS.code],
      ['O material', materialName, LIMITS.material],
      ['A unidade', unit, LIMITS.unit],
      ['A torre', towerName, LIMITS.tower],
      ['O pavimento', floorName, LIMITS.floor],
      ['A disciplina', disciplineName, LIMITS.discipline],
      ['O serviço', serviceName, LIMITS.service],
      ['A categoria', category, LIMITS.category],
    ] as const) {
      if (text !== null && text.length > max) {
        problems.push(`${name.toLowerCase()} tem mais de ${String(max)} caracteres`);
      }
    }

    if (
      problems.length > 0 ||
      kind === undefined ||
      basis === undefined ||
      quantityValue === undefined ||
      requiredDate === undefined
    ) {
      issues.push({ rowNumber, message: `${label}: ${problems.join('; ')}.` });
      continue;
    }

    const row: PurchaseLineRowData = {
      rowNumber,
      towerName,
      floorName,
      floorLevel: level.ok ? level.value : null,
      disciplineName,
      serviceName,
      materialCode: code,
      materialName,
      materialCategory: category,
      kind,
      unit,
      quantity: quantityValue,
      quantityProvisional: readProvisional(at(columns.provisional)),
      allocationBasis: basis,
      origin: 'ORCAMENTO',
      dateFollowsItem:
        readWbsList(at(columns.wbs)).length > 0 &&
        basis !== 'RATEIO_ANDARES' &&
        normalizeName(floorName) !== LEISURE_FLOOR,
      requiredDate,
      notes: notes === null ? null : clip(notes),
    };
    const segment = cleanText(at(columns.segment));

    // RN-352: a mesma (frente, serviço, material) — as faces da fachada — se soma.
    const key = [towerName, floorName, disciplineName, serviceName ?? '', code]
      .map(normalizeName)
      .join('\u0001');
    const group = groups.get(key);
    if (group === undefined) {
      addTotal(code, quantityValue);
      groups.set(key, {
        row,
        quantity: quantityValue,
        requiredDate,
        provisional: row.quantityProvisional,
        follows: row.dateFollowsItem,
        bases: new Set([basis]),
        sources: [{ rowNumber, segment }],
        notes: notes === null ? [] : [clip(notes)],
      });
      continue;
    }

    if (normalizeName(unit) !== normalizeName(group.row.unit) || kind !== group.row.kind) {
      issues.push({
        rowNumber,
        message:
          `${label}: repete a linha ${String(group.row.rowNumber)} (mesmo pavimento, disciplina, ` +
          `serviço e código) com ${
            normalizeName(unit) !== normalizeName(group.row.unit)
              ? `unidade diferente ("${unit}" e "${group.row.unit}")`
              : 'classe diferente'
          } — as duas não se somam.`,
      });
      continue;
    }
    unions += 1;
    addTotal(code, quantityValue);
    group.quantity += quantityValue;
    if (requiredDate < group.requiredDate) group.requiredDate = requiredDate;
    group.provisional ||= row.quantityProvisional;
    group.follows &&= row.dateFollowsItem;
    group.bases.add(basis);
    group.sources.push({ rowNumber, segment });
    if (notes !== null && !group.notes.includes(clip(notes))) group.notes.push(clip(notes));
  }

  const rows = [...groups.values()].map(finishGroup);
  if (rows.length === 0 && skipped.length === 0 && issues.length === 0) {
    return err(
      domainError(
        PURCHASE_TEMPLATE_ERROR.LINES_NO_ROWS,
        'A aba "Itens de compra" tem o cabeçalho, mas nenhuma linha de item abaixo dele.',
      ),
    );
  }

  return ok({
    rows,
    skipped,
    issues,
    unions,
    totals: [...totals.values()].map((total) => ({
      materialCode: total.materialCode,
      total: round(total.total),
    })),
    unionGroups: [...groups.values()].filter((group) => group.sources.length > 1).length,
    headerRowNumber: header.rowIndex + 1,
  });
}

// ── A soma das faces ────────────────────────────────────────────────────────────

/** As linhas da planilha que dão uma só linha de compra, enquanto a leitura anda. */
interface Group {
  /** A primeira linha do grupo — dela vêm o número da linha e tudo o que não se soma. */
  readonly row: PurchaseLineRowData;
  quantity: number;
  requiredDate: string;
  provisional: boolean;
  follows: boolean;
  readonly bases: Set<AllocationBasisData>;
  readonly sources: { readonly rowNumber: number; readonly segment: string | null }[];
  readonly notes: string[];
}

/**
 * Da base **menos firme** para a mais firme. Quando a soma mistura bases — o mesmo material
 * no mesmo serviço, vindo de duas composições do orçamento, uma direta e outra rateada —, a
 * linha fica com a menos firme: ela não pode se dizer `DIRETO` se parte da quantidade é
 * rateio (RN-345). É o mesmo critério da quantidade provisória, que basta uma parte ser.
 */
const BASIS_WEAKEST_FIRST: readonly AllocationBasisData[] = [
  'RATEIO_ANDARES',
  'RATEIO_OBRA',
  'LISTA',
  'DIRETO',
];

/**
 * A linha final do grupo. Uma linha só passa como veio. Várias: quantidade somada, a menor
 * data (o que se precisa primeiro), provisória se alguma o for, a base menos firme das que
 * entraram, e a data só segue o item se todas seguiam. A observação diz quantas se juntaram
 * — e quais trechos, quando há.
 */
function finishGroup(group: Group): PurchaseLineRowData {
  if (group.sources.length === 1) return group.row;

  const segments = group.sources.flatMap((source) =>
    source.segment === null ? [] : [source.segment],
  );
  const basis =
    BASIS_WEAKEST_FIRST.find((candidate) => group.bases.has(candidate)) ??
    group.row.allocationBasis;
  const merged =
    `Soma de ${String(group.sources.length)} linhas da planilha` +
    (segments.length > 0 ? ` (${segments.join(', ')})` : '') +
    ` — linhas ${group.sources.map((source) => String(source.rowNumber)).join(', ')}`;
  const mixed =
    group.bases.size > 1
      ? `Bases de cálculo misturadas (${[...group.bases].join(', ')}): vale a menos firme, ${basis}`
      : null;
  const notes = [merged, mixed, ...group.notes].filter((note) => note !== null).join('. ');

  return {
    ...group.row,
    quantity: round(group.quantity),
    requiredDate: group.requiredDate,
    quantityProvisional: group.provisional,
    allocationBasis: basis,
    dateFollowsItem: group.follows,
    notes: clip(notes),
  };
}

/** A soma de decimais acumula resíduo (0,1 + 0,2): arredonda ao milionésimo. */
function round(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

/** A observação do contrato tem teto; o que passa dele perde o fim, e não a linha. */
function clip(text: string): string {
  return text.length <= LIMITS.notes ? text : `${text.slice(0, LIMITS.notes - 1)}…`;
}

// ── As células ────────────────────────────────────────────────────────────────────

type Gate =
  | { readonly kind: 'SIM' | 'NAO' | 'CONFIRMAR' | 'EMPTY' }
  | { readonly kind: 'UNKNOWN'; readonly text: string };

/**
 * `vai_para_sistema`: Sim entra; Não e CONFIRMAR ficam só na planilha. Só o que está
 * escrito vale — "x" ou "1" **não** é Sim aqui, porque uma linha que entra por engano vira
 * ordem de compra, e uma que fica de fora aparece na contagem das ignoradas.
 */
function readGate(value: unknown): Gate {
  if (typeof value === 'boolean') return { kind: value ? 'SIM' : 'NAO' };
  const key = headerKey(value);
  if (key === '') return { kind: 'EMPTY' };
  if (key === 'sim' || key === 's') return { kind: 'SIM' };
  if (key === 'nao' || key === 'n') return { kind: 'NAO' };
  if (key.startsWith('confirmar')) return { kind: 'CONFIRMAR' };
  return { kind: 'UNKNOWN', text: rawText(value) };
}

/** `provisoria`: Sim (ou S, x, 1, verdadeiro) é provisória; o resto, inclusive o vazio, não. */
function readProvisional(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return ['sim', 's', 'x', '1', 'true', 'verdadeiro'].includes(headerKey(value));
}

function rawText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function skipReason(
  gate: 'NAO' | 'CONFIRMAR',
  material: string | null,
  materialClass: string | null,
  floorName: string | null,
): string {
  const what =
    [material, materialClass === null ? null : `classe ${materialClass}`, floorName]
      .filter((part) => part !== null)
      .join(', ') || 'linha';
  return gate === 'NAO'
    ? `Fica só na planilha (vai_para_sistema = Não): ${what}.`
    : `Marcada CONFIRMAR na planilha, não entra até ser confirmada: ${what}.`;
}

/**
 * O método de cálculo → a base do rateio. Compara pelo texto sem acento, sem caixa e sem
 * pontuação, com o `×` valendo `x`: "CPU×Q_T2" é `cpuxqt2`, "obra×169/481" é `obrax169481`.
 * Reconhece pelo começo, porque a planilha pode acrescentar uma explicação ao fim.
 */
const METHOD_PREFIXES: readonly (readonly [string, AllocationBasisData])[] = [
  ['cpux', 'DIRETO'],
  ['direto', 'DIRETO'],
  ['obrax', 'RATEIO_OBRA'],
  ['rateioobra', 'RATEIO_OBRA'],
  ['rateiodaobra', 'RATEIO_OBRA'],
  ['rateiopor', 'RATEIO_ANDARES'],
  ['rateioandar', 'RATEIO_ANDARES'],
  ['lista', 'LISTA'],
];

function basisOf(method: string): AllocationBasisData | undefined {
  const key = headerKey(method)
    .replace(/[×*]/g, 'x')
    .replace(/[^a-z0-9]/g, '');
  return METHOD_PREFIXES.find(([prefix]) => key.startsWith(prefix))?.[1];
}

/** As classes que a planilha traz e o sistema não: equipamento, despesa indireta, outros. */
const CLASSES_OUTSIDE_THE_SYSTEM = new Set(['eqp', 'di', 'out']);

/**
 * A classe do orçamento → o tipo da linha (D6, RN-349). MAT e as listas das instalações são
 * compra; ST e ST-MAT são contratação. Uma lista de instalação cuja classe a planilha escreve
 * de outro jeito ainda é material: o que a define é o método "lista Mat T2".
 */
function kindOf(
  materialClass: string | null,
  basis: AllocationBasisData | undefined,
): PurchaseKindData | undefined {
  const key = materialClass === null ? '' : headerKey(materialClass);
  if (key === 'st' || key === 'st mat' || key === 'stmat') return 'SERVICO_TERCEIRIZADO';
  if (
    key === 'mat' ||
    key.startsWith('mat ') ||
    key.startsWith('lista') ||
    key.includes('instal')
  ) {
    return 'MATERIAL';
  }
  if (basis === 'LISTA' && !CLASSES_OUTSIDE_THE_SYSTEM.has(key)) return 'MATERIAL';
  return undefined;
}
